import { Box, CardMedia, Typography } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { bookCard } from './Card.style';
import sampleBookCover from '/assets/sampleBookCover.png';
import appleIcon from '/assets/image/ic_apple.svg';
import tikuElementary from '/assets/image/tiku/tikuElementary.png';
import tikuJunior from '/assets/image/tiku/tikuJunior.png';
import tikuHigh from '/assets/image/tiku/tikuHigh.png';
import { useMemo, useCallback, useState } from 'react';
import {
  getBookCardActionButtonProps,
  getBookCardTitle,
  getWebReaderUrl,
  WEB_READER_ACTION_BUTTON_PROPS,
} from './Card.constant';
import { useSelector } from 'react-redux';
import {
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
  BaseButton,
} from '~/components/Global/Button';
import { isWeb } from '~/utils/helper/device';
import { getterIsValidLogin } from '~/store/user/userGetter';
import { openMode, useDownload } from '~/utils/hook/useDownload';
import { FileStatus } from '~/store/resource/resource.type';
import { setDownloadList } from '~/store/resource';
import { RootState, useAppDispatch } from '~/store';
import { setShowLoginDialog } from '~/store/user';
import { TDownloadableResource } from '~/types/common.type';
import { FileType } from '~/store/file/file.type';
import {
  UpdateDrawer,
  UpdateDrawerAction,
} from '~/components/Global/Drawer/UpdateDrawer';
import { useAutoExtractEffect } from '~/utils/hook/useAutoExtractEffect';
import { getDirPath } from '~/utils/helper/function';
import { useFile } from '~/utils/hook/useFile';
import { useCommon } from '~/utils/hook/useCommon';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import { downloadStartEvent, downloadStatusEvent } from '~/tracking';
import { pxToRem } from '~/theme/typography';

type TProps = {
  overview: TDownloadableResource;
  progress: TDownloadableResource['progress'];
  webReaderBookId?: string;
  isMacOnly?: boolean;
};

export const BookCard = ({
  overview,
  progress,
  webReaderBookId,
  isMacOnly = false,
}: TProps) => {
  const isWebReader = !!webReaderBookId;
  const [toggleOpenUpdate, setToggleOpenUpdate] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { resource } = overview;
  const { fileName, file, coverImg, displayName } = resource;
  const { url } = file;
  const {
    status,
    progressRatio,
    key,
    updateTime,
    totalBytes,
    installingProgressRatio,
    downloadedAt,
  } = progress;
  const {
    download,
    pause,
    resume,
    cancel,
    deleteFile,
    downloadListChangeListener,
    update,
  } = useDownload();
  const { getOneBookFiles, getTikuFiles, getFlashFiles, openPath } = useFile();
  const { openExternal } = useCommon();
  const ownInfo = useSelector((state: RootState) => state.user.ownInfo);
  const isValidLogin = useSelector(getterIsValidLogin);
  const progressTitle = useMemo(
    () =>
      isWebReader
        ? ''
        : getBookCardTitle({
            status,
            progress:
              status === FileStatus.installing
                ? installingProgressRatio
                : progressRatio,
          }),
    [isWebReader, status, installingProgressRatio, progressRatio],
  );

  const isCancellable = useMemo(
    () =>
      !isWebReader &&
      (status === FileStatus.progressing || status === FileStatus.paused),
    [isWebReader, status],
  );
  const actionButtonProps = useMemo(
    () =>
      isWebReader
        ? WEB_READER_ACTION_BUTTON_PROPS
        : getBookCardActionButtonProps(status),
    [isWebReader, status],
  );

  const hasProgress = useMemo(() => {
    return (
      status === FileStatus.progressing ||
      status === FileStatus.paused ||
      status === FileStatus.completed ||
      (status === FileStatus.installing && installingProgressRatio < 100)
    );
  }, [status, installingProgressRatio]);

  const overallProgress = useMemo(() => {
    return status === FileStatus.installing
      ? installingProgressRatio
      : progressRatio;
  }, [status, installingProgressRatio, progressRatio]);

  const checkPathType = (files: string[]) => {
    const { years, subjects, eduCodes, grades } = overview.resource;
    let filePath;
    if (displayName.includes('題庫')) {
      filePath = files.find(path => {
        return path.includes(displayName.split('.')[0]);
      });
    } else {
      filePath = files.find(path => {
        let year: string = '';
        const eduCodeMatch = eduCodes.some(item => path.includes(item));
        const yearMatch = years.some(item => {
          if (path.includes(item?.slice(0, 3))) {
            year = item;
            return true;
          }
        });
        const subjectMatch = subjects.some(item => path.includes(item));
        const gradeMatch = grades.some(item => {
          if (path.includes(`${item?.slice(0, 1)}${year[year.length - 1]}`)) {
            return true;
          }
        });

        return eduCodeMatch && yearMatch && subjectMatch && gradeMatch;
      });
    }
    console.log('checkPathType', filePath, grades, overview.resource);
    return filePath;
  };

  const handleOpen = useCallback(() => {
    if (isWeb) return;
    void (async () => {
      try {
        const oneBookFiles = await getOneBookFiles();
        const tikuFiles = await getTikuFiles();
        const flashFiles = await getFlashFiles();
        let pathType = FileType.tiku;
        const filesInfo = [
          { type: FileType.oneBook, paths: oneBookFiles },
          { type: FileType.flash, paths: flashFiles },
          { type: FileType.tiku, paths: tikuFiles },
        ];

        let coverPath;

        for (const { type, paths } of filesInfo) {
          coverPath = checkPathType(paths);
          if (coverPath) {
            pathType = type;
            break;
          }
        }
        switch (pathType) {
          case FileType.oneBook:
            coverPath = coverPath?.replace('OneBook\\cover.jpg', 'OneBook.exe');
            break;
          case FileType.flash:
            coverPath = coverPath?.replace(
              'ToolMon\\thumbnails\\nanibook.jpg',
              'Nani_index.exe',
            );
            break;
          default:
            break;
        }
        coverPath && openPath(coverPath);
      } catch (error) {
        console.error('Error in handleOpen:', error);
      }
    })();
  }, []);

  const handleOpenPath = () => {
    if (key) {
      openPath(getDirPath({ key, fileName }));
    }
  };

  const handleDelete = () => {
    if (key) {
      deleteFile({ filePath: key, filename: fileName });
    }
  };

  const handleUpdate = () => {
    if (key) {
      update({
        filePath: key,
        filename: fileName,
        resource,
        mode: openMode.link,
      });
    }
  };

  const handleDownload = () => {
    downloadStartEvent({
      event_type: '書本卡片下載',
      edu: resource.eduCodes[0],
      subject: resource.subjects[0],
      category: resource.resources1[0],
      resources_label: resource.resources2[0],
      resources_sub_label: resource.resources3[0],
      resources_url: resource.file.url,
      resources_name: resource.fileName,
    });

    void download({ resource, mode: openMode.link });
  };

  const handleUpdateDrawerAction = (action: UpdateDrawerAction) => {
    switch (action) {
      case UpdateDrawerAction.delete:
        handleDelete();
        break;
      case UpdateDrawerAction.open:
        handleOpen();
        break;
      case UpdateDrawerAction.openPath:
        handleOpenPath();
        break;
      case UpdateDrawerAction.download:
        handleDownload();
        break;
      case UpdateDrawerAction.update:
        handleUpdate();
        break;
      default:
        break;
    }
  };

  const handleAction = useCallback(() => {
    if (!ownInfo || !isValidLogin) {
      dispatch(setShowLoginDialog(true));
      return;
    }
    if (webReaderBookId) {
      openExternal(getWebReaderUrl(webReaderBookId));
      return;
    }
    if (isWeb) {
      handleDownload();
    } else {
      downloadListChangeListener(({ downloadList }) => {
        dispatch(setDownloadList(downloadList));
      });
      if (status === FileStatus.non || status === FileStatus.cancel) {
        handleDownload();
      } else if (status === FileStatus.progressing) {
        if (key) {
          pause(key);
          downloadStatusEvent({
            event_type: '暫停',
            resources_url: resource.file.url,
            resources_name: resource.fileName,
          });
        }
      } else if (status === FileStatus.paused) {
        if (key) {
          resume(key);
          downloadStatusEvent({
            event_type: '繼續',
            resources_url: resource.file.url,
            resources_name: resource.fileName,
          });
        }
      } else if (status === FileStatus.finish) {
        // TODO: 確認開啟行為
        if (overview.file && overview.file?.type === FileType.flash) {
          handleOpen();
        } else {
          handleOpenPath();
        }
      }
    }
  }, [
    status,
    key,
    resource,
    ownInfo,
    isValidLogin,
    isWebReader,
    webReaderBookId,
  ]);

  // TODO: 安裝時的進度條沒有正常顯示

  useAutoExtractEffect({ status, key, fileName, url, updateTime, totalBytes });

  const getCardImage = (eduCodes: resourceEduCode) => {
    switch (eduCodes) {
      case resourceEduCode.E:
        return tikuElementary;
      case resourceEduCode.J:
        return tikuJunior;
      case resourceEduCode.H:
        return tikuHigh;
      default:
        return tikuElementary;
    }
  };

  return (
    <>
      <bookCard.Container>
        <bookCard.Head
          title={displayName}
          titleTypographyProps={{ title: displayName }}
        />
        <bookCard.Content>
          {!progressTitle && !isWeb && !isWebReader && (
            <bookCard.MoreButton>
              <MoreHoriz
                sx={{ fill: '#7E5920' }}
                onClick={() => {
                  setToggleOpenUpdate(true);
                }}
              />
            </bookCard.MoreButton>
          )}
          {!!progressTitle && (
            <bookCard.ProgressMask>
              <Typography fontSize="inherit" fontWeight="700">
                {progressTitle}
              </Typography>
              {hasProgress && (
                <>
                  <bookCard.ProgressBar
                    variant="determinate"
                    value={overallProgress}
                    status={status}
                  />
                  <Typography fontSize="inherit">
                    {overallProgress} %
                  </Typography>
                </>
              )}
            </bookCard.ProgressMask>
          )}
          {isMacOnly && (
            <bookCard.MacOnlyBadge>
              <Box
                component="img"
                src={appleIcon}
                alt="apple icon"
                sx={{ width: '36px', height: '36px' }}
              />
              mac 專用
            </bookCard.MacOnlyBadge>
          )}
          <CardMedia
            component="img"
            src={
              resource.resources1[0] === '題庫光碟'
                ? getCardImage(resource.eduCodes[0])
                : coverImg
                  ? coverImg
                  : sampleBookCover
            }
            sx={{ height: '238px', aspectRatio: '209/238' }}
          />
        </bookCard.Content>
        <bookCard.Action>
          {!!actionButtonProps.text && (
            <BaseButton
              variant={actionButtonProps.variant}
              color={actionButtonProps.color}
              text={actionButtonProps.text}
              onClick={handleAction}
              width={actionButtonProps.width}
              style={{ fontSize: pxToRem(18) }}
            />
          )}
          {isCancellable && (
            <BaseButton
              text="取消"
              color={BASE_BUTTON_COLOR.PRIMARY}
              variant={BASE_BUTTON_VARIANT.FILLED}
              width="96px"
              style={{ fontSize: pxToRem(18) }}
              onClick={() => {
                if (key) {
                  cancel(key);
                  downloadStatusEvent({
                    event_type: '取消下載',
                    resources_url: resource.file.url,
                    resources_name: resource.fileName,
                  });
                }
              }}
            />
          )}
        </bookCard.Action>
      </bookCard.Container>
      {!isWeb && !isWebReader && (
        <UpdateDrawer
          open={toggleOpenUpdate}
          onClose={() => setToggleOpenUpdate(false)}
          fileName={fileName}
          uploadedAt={file.fileUploadTime}
          downloadedAt={downloadedAt}
          handleAction={handleUpdateDrawerAction}
        />
      )}
    </>
  );
};
